import React from 'react';
import {type CellContext, type RowData} from '@tanstack/react-table';
import {useArrowFocusTrapItem} from '@netx/core-components/ArrowFocusTrapNext';
import {
	StatusSwitch,
	type StatusSwitchProps,
} from '@netx/core-components/StatusSwitch';
import {silenceEvent, stopPropagation} from '@netx/core-next/utils/browser';
import {splitEventHandler} from '@netx/core-next/utils/fn';

export interface StatusSwitchCellProps<TData extends RowData>
	extends CellContext<TData, any> {
	SwitchProps?: Partial<StatusSwitchProps>;
}

const StatusSwitchCellPrimitive = <TData extends RowData>({
	SwitchProps,
	...props
}: StatusSwitchCellProps<TData>) => {
	const arrowFocusTrapItem = useArrowFocusTrapItem({
		index: props.row.index,
	});

	if (!props.row.original) return;

	return (
		// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
		<div onMouseDown={stopPropagation}>
			<StatusSwitch
				tabIndex={arrowFocusTrapItem.attributes.tabIndex}
				{...SwitchProps}
				onBlur={splitEventHandler(
					SwitchProps?.onBlur,
					arrowFocusTrapItem.actionAttributes.onBlur,
				)}
				onChange={splitEventHandler(SwitchProps?.onChange, silenceEvent)}
				onClick={splitEventHandler(
					SwitchProps?.onClick,
					stopPropagation,
					(event) => {
						arrowFocusTrapItem.setActive();
					},
				)}
				onFocus={splitEventHandler(
					SwitchProps?.onFocus,
					arrowFocusTrapItem.actionAttributes.onFocus,
				)}
			/>
		</div>
	);
};

export const StatusSwitchCell = StatusSwitchCellPrimitive;
export default StatusSwitchCell;
